import { ApiService } from "../model/Api.model";
import { AxiosInstance } from "axios";
import { AttorneysParam } from "../model/AttorneysParam.model";
import { IDocument } from "../interface/IDocument";
import { saveByteArray } from "src/util/saveByteArray";
import { IDocumentUpload, SimpleDocumentUpload } from "../interface/IDocumentUpload";
import { FilesCategoryEnum, FilesCategoryEnumLabel } from "../enum/FilesCategoryEnum";
import { generateQueryString } from "src/util/helper";
import { ITrade } from '../interface/trade/ITrade';
import { Dayjs } from "dayjs";
import { IRequestActivationP2 } from "../interface/RequestActivationP2";

enum DocumentServiceEnum {
  findMarriageCertificate = "/find-marriage-certificate?tradeId=",
  downloadTradeDocument = "/",
  downloadDealDocument = "/deal/",
  proxyDownload = "/proxy-download",
  downloadPaperclipDocument = "/download-paperclip-document/",
  downloadContractWithAttorneys = "/download-with-attorneys/",
  downloadUniproofDocument = "/dowload-uniproof-document/{documentId}",
  uploadMarriageCertificate = "/upload-marriage-certificate/",
  uploadLetterOfAttorney = "/upload-letter-of-attorney",
  uploadLegalProcess = "/upload-Legal-process",
  uploadContratoEscrituraPublica = "/contrato-escritura-publica/",
  uploadDiligenceDocument = "/upload-diligence-document",
  uploadProcessDocument = "/upload-process-document",
  uploadPublicDeed = "/upload-public-deed",
  uploadTedReceipt = "/upload-ted-receipt",
  uploadCessionTerm = "/upload-contract",
  getAll = "/get-documents",
  getByFileKey = "/",
  getByFileKeyToken = "/get-document-by-file-key/",
  getById = "/get-document/",
  getDocumentByTradeIdAndCategory = "/find-document-by-trade-and-categegory?tradeId={tradeId}&category={category}",
  getTermoCessao = "/dowload-termo-cessao/{tradeId}",
  getTermoAceite = "/dowload-termo-aceite/{tradeId}",
  getInstrumentoPublicoCessao = "/dowload-instrumento-publico-cessao/{tradeId}",
  getInstrumentoPublicoMandato = "/dowload-instrumento-publico-mandato/{tradeId}",
  getUniproofDocument = "/dowload-instrumento-publico-mandato/{tradeId}",
  uploadGeneric = "/upload",
  getDocumentTags = "/get-tags",
  getAllDocumentsCategories = "/get-categories",
  uploadTed = "/upload-ted",
  ted = "/ted",
  sendP2RequestActivation = "/submit-p2-activation-request"
}

export default class DocumentService extends ApiService {
  constructor(restClient: AxiosInstance) {
    super(restClient, "/document");
  }

  public getAll = async (): Promise<IDocument[]> => await this.GET(DocumentServiceEnum.getAll);
  public getAllDocumentsCategory = async (): Promise<string[]> => await this.GET(DocumentServiceEnum.getAllDocumentsCategories);
  public getById = async (id: number): Promise<IDocument> => await this.GET(DocumentServiceEnum.getById + id);

  public putUploadMarriageCertificate = async (
    id: number,
    category: string,
    file: string,
    description: string,
    storageType: string,
    tradeId: number,
    fileName: string
  ): Promise<IDocument[]> =>
    await this.PUT(DocumentServiceEnum.uploadMarriageCertificate + tradeId, {
      id,
      category,
      file,
      description,
      storageType,
      tradeId,
      fileName
    });

  public getDocumentByTradeIdAndCategory = async (tradeId, category) =>
    this.GET(DocumentServiceEnum.getDocumentByTradeIdAndCategory.replace("{tradeId}", tradeId).replace("{category}", category));
  public findMarriageCertificate = async tradeId => this.GET(DocumentServiceEnum.findMarriageCertificate + tradeId);
  public getByFileKey = async fileKey => this.GET(DocumentServiceEnum.getByFileKey + fileKey);

  public proxyDownload = async (uri: string, filename: string) => {
    const data = await this.POST(DocumentServiceEnum.proxyDownload, { uri }, "blob");

    saveByteArray(filename, data);
    return data;
  }

  public downloadDocument = async (document: IDocument, tradeId?: number, user?: string) => {
    const filename = (tradeId ? `${tradeId}_` : "") + document.fileName ? document.fileName : "documento" + (user ? `_${user}` : "") + ".docx";
    const data = await this.GET(DocumentServiceEnum.downloadTradeDocument + (document.file && document.file !== "null" ? document.file : document.path), "blob");

    saveByteArray(filename, data);
    return data;
  };

  public downloadDealDocument = async (document: IDocument, user?: string) => {
    const filename = document.fileName ? document.fileName : "documento" + (user ? `_${user}` : "") + ".docx";
    const data = await this.GET(DocumentServiceEnum.downloadDealDocument + document.path, "blob");

    saveByteArray(filename, data);
    return data;
  };

  public downloadTradeDocument = async (document: IDocument) => {
    const data = await this.GET(DocumentServiceEnum.downloadTradeDocument + document.path, "blob");

    saveByteArray(document.fileName, data);
    return data;
  };

  public downloadDoc = async (path: string, fileName: string) => {
    const data = await this.GET(DocumentServiceEnum.downloadTradeDocument + path, "blob");

    saveByteArray(fileName, data);
    return data;
  };

  public downloadPaperclipDocument = async (category: FilesCategoryEnum, tradeId?: number, user?: string) => {
    const filename = (tradeId ? `${tradeId}_` : "") + FilesCategoryEnumLabel.get(category) + (user ? `_${user}` : "") + ".docx";
    const endpoint = DocumentServiceEnum.downloadPaperclipDocument + tradeId + "/" + FilesCategoryEnum[category];
    const data = await this.GET(endpoint, "blob");

    saveByteArray(filename, data);
    return data;
  };

  public downloadContractWithAttorneys = async (category: FilesCategoryEnum, tradeId?: number, user?: string, attorneys?: AttorneysParam | null) => {
    attorneys = attorneys ? attorneys : null;
    const filename = (tradeId ? `${tradeId}_` : "") + FilesCategoryEnumLabel.get(category) + (user ? `_${user}` : "") + ".docx";
    const endpoint = DocumentServiceEnum.downloadContractWithAttorneys + tradeId + "/" + category;
    const data = await this.PUT(endpoint, attorneys, "blob");

    saveByteArray(filename, data);
    return data;
  };

  public downloadUniproof = async documentId => {
    const data = await this.GET(DocumentServiceEnum.downloadUniproofDocument.replace("{documentId}", documentId), "blob");
    saveByteArray("UniproofDocument.pdf", data);
    return data;
  };

  public getByFileKeyToken = async fileKeyToken => await this.GET(DocumentServiceEnum.getByFileKeyToken + fileKeyToken);
  public putUploadLetterOfAttorney = async file => await this.PUT(DocumentServiceEnum.uploadLetterOfAttorney, file);
  public putUploadLegalProcess = async file => await this.PUT(DocumentServiceEnum.uploadLegalProcess, file);

  public putUploadDiligenceDocument = async file => await this.PUT(DocumentServiceEnum.uploadDiligenceDocument, file);
  public putUploadTedReceipt = async file => await this.PUT(DocumentServiceEnum.uploadTedReceipt, file);
  public putUploadProcessDocument = async data => await this.PUT(DocumentServiceEnum.uploadProcessDocument, data);
  public putUploadPublicDeed = async data => await this.PUT(DocumentServiceEnum.uploadPublicDeed, data);
  public putUploadCessionTerm = async data => await this.PUT(DocumentServiceEnum.uploadCessionTerm, data);
  public putUploadGeneric = async (data: Omit<IDocumentUpload, "id" | "path">) => await this.PUT(DocumentServiceEnum.uploadGeneric, data);

  public getDocumentTags = async () => await this.GET(DocumentServiceEnum.getDocumentTags);

  public uploadTed = async (disbursementDate: Dayjs, document: SimpleDocumentUpload) => await this.POST(DocumentServiceEnum.uploadTed, { disbursementDate, ...document });

  public ted = async ({ id, tradePricing: { pricing: { pricingDisbursements } } }: ITrade, fileKey: string) => {
    const filename = (id ? `${id}_` : "") + pricingDisbursements.find(f => f.tedDocumentKey === fileKey)?.trigger + "TED.pdf";
    const data = await this.GET(`${DocumentServiceEnum.ted}?${generateQueryString({ tradeId: id, fileKey })}`, "blob");

    saveByteArray(filename, data);
    return data;
  };

  public sendP2RequestActivation = (request: IRequestActivationP2) => {
    return this.POST(`${DocumentServiceEnum.sendP2RequestActivation}`, request);
  };
}
